import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, callAdminFunction } from '../supabaseClient';
import Navbar from './Navbar';
import './AdminUsuarios.css';
import { FaEdit, FaTrash, FaKey, FaPlus, FaSearch } from 'react-icons/fa';
import CreateUserModal from './CreateUserModal';
import ResetPasswordModal from './ResetPasswordModal';

function AdminUsuarios() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [isNextDisabled, setIsNextDisabled] = useState(false);
    const [isPrevDisabled, setIsPrevDisabled] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [resetPasswordUser, setResetPasswordUser] = useState(null);
    const [userRole, setUserRole] = useState('');

    const ROWS_PER_PAGE = 10;
    const isLoadingRef = useRef(false);
    const searchTimeoutRef = useRef(null);

    // Check if current user is admin
    useEffect(() => {
        const checkUserRole = async () => {
            try {
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                    const { data, error } = await supabase
                        .from('users')
                        .select('role')
                        .eq('wsp_email', user.email)
                        .single();

                    if (error) throw error;
                    setUserRole(data.role);

                    // Redirect if not admin
                    if (data.role !== 'admin') {
                        window.location.href = '/dashboard';
                    }
                }
            } catch (error) {
                console.error('Error checking user role:', error.message);
            }
        };

        checkUserRole();
    }, []);

    // Fetch users from Supabase
    const fetchUsers = useCallback(async (page = 0, search = '') => {
        if (isLoadingRef.current) return;
        isLoadingRef.current = true;

        setIsPageLoading(true);
        setLoading(true);

        try {
            const from = page * ROWS_PER_PAGE;
            const to = from + ROWS_PER_PAGE - 1;

            // Get total count
            const { count } = await supabase
                .from('users')
                .select('*', { count: 'exact', head: true })
                .ilike('wsp_email', search ? `%${search}%` : '%')
                .neq('role', 'Empleado');

            setTotalCount(count);

            if (from >= count) {
                setIsNextDisabled(true);
                setError('No hay más usuarios para mostrar');
                return;
            }

            // Fetch users
            let query = supabase
                .from('users')
                .select('*')
                .neq('role', 'Empleado');

            if (search) {
                query = query.ilike('wsp_email', `%${search}%`);
            }

            const { data: fetchedUsers, error } = await query
                .range(from, to)
                .order('wsp_email', { ascending: true });

            if (error) throw error;

            setUsers(fetchedUsers);
            setIsNextDisabled(from + fetchedUsers.length >= count);
            setIsPrevDisabled(page === 0);
            setHasMore(from + fetchedUsers.length < count);
            setError(null);

        } catch (error) {
            console.error('Error:', error);
            setError('Error fetching users: ' + error.message);
        } finally {
            setLoading(false);
            setIsPageLoading(false);
            isLoadingRef.current = false;
        }
    }, []);

    // Search handler
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            fetchUsers(0, searchTerm);
        }, 500);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm, fetchUsers]);

    // Initial load
    useEffect(() => {
        if (userRole === 'admin') {
            fetchUsers(0, '');
        }
    }, [userRole, fetchUsers]);

    // Pagination handlers
    const handlePreviousPage = useCallback(() => {
        if (currentPage > 0 && !isPrevDisabled && !isPageLoading && !isLoadingRef.current) {
            const newPage = currentPage - 1;
            setCurrentPage(newPage);
            fetchUsers(newPage, searchTerm);
        }
    }, [currentPage, isPrevDisabled, isPageLoading, searchTerm, fetchUsers]);

    const handleNextPage = useCallback(() => {
        if (hasMore && !isNextDisabled && !isPageLoading && !isLoadingRef.current) {
            const newPage = currentPage + 1;
            setCurrentPage(newPage);
            fetchUsers(newPage, searchTerm);
        }
    }, [currentPage, hasMore, isNextDisabled, isPageLoading, searchTerm, fetchUsers]);

    // Search handler
    const handleSearchChange = useCallback((e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(0);
    }, []);

    // Delete user
    const handleDeleteUser = async (email) => {
        try {
            // First, get the auth user ID
            const { data: authUsers } = await callAdminFunction('listUsers', {});

            // Find the matching user by email
            const authUser = authUsers.users.find(user => user.email === email);

            if (authUser) {
                // Delete from Supabase auth
                await callAdminFunction('deleteUser', { userId: authUser.id });
            } else {
                // If no auth user found, just delete from the custom table
                console.warn("User not found in auth system, deleting only from custom table");
            }

            // Delete from users table
            const { error: dbError } = await supabase
                .from('users')
                .delete()
                .eq('wsp_email', email);

            if (dbError) throw dbError;

            // Refresh the list
            fetchUsers(currentPage, searchTerm);
        } catch (error) {
            console.error("Delete user error:", error);
            setError('Error deleting user: ' + error.message);
        }
    };

    // Create user
    const handleCreateUser = async (userData) => {
        try {
            // The user was already created in the CreateUserModal component,
            // so we don't need to create it again here.
            // Just refresh the list
            setShowCreateModal(false);
            fetchUsers(0, '');
            setCurrentPage(0);
            return true;
        } catch (error) {
            setError('Error refreshing user list: ' + error.message);
            return false;
        }
    };

    // Reset password
    const handleResetPassword = async (email, newPassword) => {
        try {
            // Get the auth user information to find the UUID
            const { data: authUsers } = await callAdminFunction('listUsers', {});

            // Find the matching user by email to get their UUID
            const authUser = authUsers.users.find(user => user.email === email);

            if (!authUser) throw new Error("Usuario no encontrado en el sistema de autenticación");

            // Now reset the password using the UUID
            await callAdminFunction('resetPassword', {
                userId: authUser.id,
                newPassword: newPassword
            });

            setResetPasswordUser(null);
            return true;
        } catch (error) {
            console.error("Password reset error:", error);
            setError('Error resetting password: ' + error.message);
            return false;
        }
    };

    if (userRole !== 'admin') {
        return <div>Acceso denegado. Esta página es solo para administradores.</div>;
    }

    return (
        <div className="admin-usuarios">
            <Navbar />
            <div className="admin-content">
                <h1>Administración de gerencias</h1>

                <div className="admin-header">
                    <div className="search-container">
                        <div className="search-input-wrapper">
                            <FaSearch className="search-icon" />
                            <input
                                type="search"
                                placeholder="Buscar usuario por correo"
                                className="search-input"
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                        </div>
                    </div>

                    <button
                        className="new-user-button"
                        onClick={() => setShowCreateModal(true)}
                    >
                        <FaPlus /> Nuevo Gerente
                    </button>
                </div>

                <div className="table-container">
                    {error && <div className="error-message">{error}</div>}
                    {loading ? (
                        <div className="loading-message">Cargando usuarios...</div>
                    ) : (
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th>Correo electrónico</th>
                                    <th>Rol</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user) => (
                                    <tr key={user.wsp_email}>
                                        <td>{user.wsp_email}</td>
                                        <td>{user.role}</td>
                                        <td>
                                            <div className="action-buttons">
                                                <button
                                                    className="reset-button"
                                                    title="Restablecer contraseña"
                                                    onClick={() => setResetPasswordUser(user)}
                                                >
                                                    <FaKey className="reset-icon" />
                                                </button>

                                                <button
                                                    className={`delete-button ${user.role === 'admin' ? 'disabled' : ''}`}
                                                    title={user.role === 'admin' ? "No se puede eliminar la cuenta de administrador" : "Eliminar usuario"}
                                                    onClick={() => {
                                                        if (user.role !== 'admin' &&
                                                            window.confirm(`¿Está seguro que desea eliminar al usuario ${user.wsp_email}?`)) {
                                                            handleDeleteUser(user.wsp_email);
                                                        }
                                                    }}
                                                    disabled={user.role === 'admin'}
                                                >
                                                    <FaTrash className="delete-icon" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="pagination">
                    <button
                        className={`pagination-button ${isPrevDisabled ? 'disabled' : ''}`}
                        onClick={handlePreviousPage}
                        disabled={isPrevDisabled || isPageLoading}
                    >
                        Atrás
                    </button>
                    <span className="page-info">
                        Página {currentPage + 1} de {Math.ceil(totalCount / ROWS_PER_PAGE) || 1}
                    </span>
                    <button
                        className={`pagination-button ${isNextDisabled ? 'disabled' : ''}`}
                        onClick={handleNextPage}
                        disabled={isNextDisabled || isPageLoading}
                    >
                        Siguiente
                    </button>
                </div>
            </div>

            {/* Create User Modal */}
            {showCreateModal && (
                <CreateUserModal
                    onClose={() => setShowCreateModal(false)}
                    onSave={handleCreateUser}
                />
            )}

            {/* Reset Password Modal */}
            {resetPasswordUser && (
                <ResetPasswordModal
                    user={resetPasswordUser}
                    onClose={() => setResetPasswordUser(null)}
                    onSave={handleResetPassword}
                />
            )}
        </div>
    );
}

export default AdminUsuarios;